import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme, IconButton, Tooltip } from '@mui/material';
import * as d3 from 'd3';
import type { NumberValue } from 'd3';

type ChartPoint = {
    timestamp: string;
    prices: Record<string, number>;
    [key: string]: any;
};

type ChartDataResult = {
    chartData: ChartPoint[];
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
};

interface ParsedChartPoint extends ChartPoint {
    parsedTimestamp: Date;
}

interface PerformanceChartProps {
    data: ChartDataResult;
    portfolioData: ChartDataResult;
    benchmarkData: ChartDataResult;
    enabledClusters: Record<number, boolean>;
    showCombinedStrategy: boolean;
}

export const PerformanceChart: React.FC<PerformanceChartProps> = ({
    data,
    portfolioData,
    benchmarkData,
    enabledClusters,
    showCombinedStrategy,
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    const [hoverLine, setHoverLine] = useState<string | null>(null);
    const [isLogScale, setIsLogScale] = useState(false);

    const hasCombinedStrategy = useMemo(() => {
        return showCombinedStrategy;
    }, [showCombinedStrategy]);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!svgRef.current || data.chartData.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 20, right: 50, bottom: 40, left: 50 };
        const height = 400 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const parseTime = d3.timeParse('%Y-%m-%dT%H:%M:%S.%LZ');

        // Parse and normalize strategy data
        const parsedData: ParsedChartPoint[] = data.chartData.map(d => ({
            ...d,
            parsedTimestamp: parseTime(d.timestamp) || new Date(d.timestamp)
        }));

        // Normalize function - converts prices to base 100 starting from first value
        const normalizeData = (dataArray: any[], firstValue: number) => {
            if (!firstValue || firstValue === 0 || !isFinite(firstValue)) return dataArray;
            return dataArray.map(d => ({
                ...d,
                price: (d.price / firstValue) * 100
            }));
        };

        const normalizedStrategyData = parsedData.map((d, i) => ({
            ...d,
            price: ((d.combinedReturn || 1) - 1) * 100 + 100  // Convert to base 100
        }));

        // Parse and normalize portfolio data
        let normalizedPortfolioData: ParsedChartPoint[] = [];
        if (portfolioData && portfolioData.chartData.length > 0) {
            const parsedPortfolioData: ParsedChartPoint[] = portfolioData.chartData.map(d => ({
                ...d,
                parsedTimestamp: parseTime(d.timestamp) || new Date(d.timestamp)
            }));

            const portfolioFirstPrice = parsedPortfolioData.length > 0
                ? Object.values(parsedPortfolioData[0].prices)[0] as number
                : 100;

                normalizedPortfolioData = parsedPortfolioData.map((d, i) => ({
                    ...d,
                    price: ((d.combinedReturn || 1) - 1) * 100 + 100 // Convert to base 100
                }));
        }

        // Parse and normalize benchmark data
        let normalizedBenchmarkData: ParsedChartPoint[] = [];
        if (benchmarkData && benchmarkData.chartData.length > 0) {
            const parsedBenchmarkData: ParsedChartPoint[] = benchmarkData.chartData.map(d => ({
                ...d,
                parsedTimestamp: parseTime(d.timestamp) || new Date(d.timestamp)
            }));
            const benchmarkFirstPrice = parsedBenchmarkData.length > 0
                ? Object.values(parsedBenchmarkData[0].prices)[0] as number
                : 100;

                normalizedBenchmarkData = parsedBenchmarkData.map((d, i) => ({
                    ...d,
                    price: ((d.combinedReturn || 1) - 1) * 100 + 100  // Convert to base 100
                }));
        }

        // Calculate combined min/max for normalized data
        const allNormalizedPrices = [
            ...normalizedStrategyData.map(d => d.price),
            ...normalizedPortfolioData.map(d => d.price),
            ...normalizedBenchmarkData.map(d => d.price)
        ].filter(p => !isNaN(p) && isFinite(p));

        // Replace the domain calculation section (around line 115-120)
        const minNormalizedPrice = Math.max(0, Math.min(...allNormalizedPrices));  // Enforce 0 minimum (representing -100%)
        const maxNormalizedPrice = Math.max(...allNormalizedPrices);
        const priceBuffer = (maxNormalizedPrice - minNormalizedPrice) * 0.05;  // Reduced buffer

        // Use timestamps from all series so the x-axis covers the full date range
        const allTimestamps = [
            ...normalizedStrategyData.map(d => d.parsedTimestamp),
            ...normalizedPortfolioData.map(d => d.parsedTimestamp),
            ...normalizedBenchmarkData.map(d => d.parsedTimestamp),
        ];

        const x = d3
            .scaleTime()
            .domain(d3.extent(allTimestamps) as [Date, Date])
            .range([0, innerWidth]);

        // Update the y scale definition
        const y = isLogScale
            ? d3.scaleLog()
                .domain([Math.max(1, minNormalizedPrice), maxNormalizedPrice + priceBuffer])  // Start at 1 for log scale (representing -99%)
                .range([height, 0])
                .nice()
            : d3.scaleLinear()
                .domain([Math.max(0, minNormalizedPrice - priceBuffer), maxNormalizedPrice + priceBuffer])
                .range([height, 0])
                .nice();

        // Add this after the y scale definition
        const logDomain = y.domain();
        const logTickValues = [1, 2, 5, 10, 20, 50, 80, 100, 150, 200, 300, 500, 1000, 5000, 10000]
            .filter(v => v >= logDomain[0] && v <= logDomain[1]);

        const yAxis = isLogScale
            ? d3.axisLeft(y)
                .tickValues(logTickValues)
                .tickFormat(d => `${((d as number)).toFixed(0)}%`)
            : d3.axisLeft(y)
                .ticks(5)
                .tickFormat(d => `${((d as number) - 100).toFixed(0)}%`);
        // Then use it for the axis
        g.append('g').call(yAxis);

        // Grid
        const gridOpacity = 0.3;
        g.selectAll('.grid-line-vertical')
            .data(x.ticks(10))
            .join('line')
            .attr('class', 'grid-line-vertical')
            .attr('x1', d => x(d))
            .attr('x2', d => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', gridOpacity);


        // Replace the horizontal grid lines section (around line 140)
        g.selectAll('.grid-line-horizontal')
            .data(y.ticks(5))  // Reduced from 8 to 5
            .join('line')
            .attr('class', 'grid-line-horizontal')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', d => y(d))
            .attr('y2', d => y(d))
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', gridOpacity);

        // Line generators
        const priceLine = d3
            .line<ParsedChartPoint>()
            .x(d => x(d.parsedTimestamp))
            .y(d => y(d.price))
            .curve(d3.curveMonotoneX);

        // Strategy price line (primary color, thick)
        g.append('path')
            .datum(normalizedStrategyData)
            .attr('class', 'strategy-line')
            .attr('fill', 'none')
            .attr('stroke', theme.palette.text.primary)
            .attr('stroke-width', 2)
            .attr('stroke-opacity', hoverLine && hoverLine !== 'strategy' ? 0.3 : 1)
            .attr('d', priceLine);

        // Portfolio line (if data exists)
        if (normalizedPortfolioData.length > 0) {
            g.append('path')
                .datum(normalizedPortfolioData)
                .attr('class', 'portfolio-line')
                .attr('fill', 'none')
                .attr('stroke', theme.palette.primary.main)
                .attr('stroke-width', 2)
                .attr('stroke-opacity', hoverLine && hoverLine !== 'portfolio' ? 0.3 : 1)
                .attr('d', priceLine);
        }

        // Benchmark line (if data exists)
        if (normalizedBenchmarkData.length > 0) {
            g.append('path')
                .datum(normalizedBenchmarkData)
                .attr('class', 'benchmark-line')
                .attr('fill', 'none')
                .attr('stroke', theme.palette.warning.main)
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '5')
                .attr('stroke-opacity', hoverLine && hoverLine !== 'benchmark' ? 0.3 : 1)
                .attr('d', priceLine);
        }

        // Bottom axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x));

        // Tooltip
        const tooltip = g.append('g')
            .attr('class', 'tooltip')
            .style('display', 'none');

        tooltip.append('rect')
            .attr('fill', 'rgba(0, 0, 0, 0.8)')
            .attr('rx', 4)
            .attr('ry', 4);

        tooltip.append('text')
            .attr('fill', 'white')
            .attr('font-size', '12px')
            .attr('text-anchor', 'start');

        g.append('rect')
            .attr('width', innerWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .on('mousemove', function (event) {
                const [mouseX] = d3.pointer(event, this);
                const date = x.invert(mouseX);

                const findClosestPoint = (dataArray: any[]) => {
                    if (!dataArray || dataArray.length === 0) return null;
                    let closestPoint = dataArray[0];
                    let minDistance = Math.abs(closestPoint.parsedTimestamp.getTime() - date.getTime());

                    dataArray.forEach(point => {
                        const distance = Math.abs(point.parsedTimestamp.getTime() - date.getTime());
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestPoint = point;
                        }
                    });
                    return closestPoint;
                };

                const strategyPoint = findClosestPoint(normalizedStrategyData);
                const portfolioPoint = findClosestPoint(normalizedPortfolioData);
                const benchmarkPoint = findClosestPoint(normalizedBenchmarkData);

                const tooltipLines = [];
                tooltipLines.push(`Date: ${date.toLocaleDateString()}`);

                if (strategyPoint) {
                    tooltipLines.push(`Strategy: ${((strategyPoint.price - 100)).toFixed(2)}%`);
                    
                    // Add asset distribution if available
                    if (strategyPoint.targetDistribution && Object.keys(strategyPoint.targetDistribution).length > 0) {
                        const sortedAssets = Object.entries(strategyPoint.targetDistribution)
                            .sort(([, a], [, b]) => (b as number) - (a as number));
                        
                        sortedAssets.forEach(([asset, allocation]) => {
                            tooltipLines.push(`${asset.toUpperCase()}: ${((allocation as number) * 100).toFixed(1)}%`);
                        });
                    }
                }
                
                if (portfolioPoint) {
                    tooltipLines.push('');
                    tooltipLines.push(`Portfolio: ${((portfolioPoint.price - 100)).toFixed(2)}%`);
                }
                if (benchmarkPoint) {
                    tooltipLines.push('');
                    tooltipLines.push(`Benchmark: ${((benchmarkPoint.price - 100)).toFixed(2)}%`);
                }

                const textElement = tooltip.select('text');
                textElement.selectAll('tspan').remove();

                tooltipLines.forEach((line, i) => {
                    textElement.append('tspan')
                        .attr('x', 8)
                        .attr('y', 15 + i * 14)
                        .text(line);
                });

                const bbox = (textElement.node() as SVGTextElement).getBBox();
                tooltip.select('rect')
                    .attr('width', bbox.width + 16)
                    .attr('height', bbox.height + 8);

                const tooltipX = Math.min(mouseX + 10, innerWidth - bbox.width - 16);
                const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, height - bbox.height - 16));

                tooltip
                    .attr('transform', `translate(${tooltipX}, ${tooltipY})`)
                    .style('display', null);
            })
            .on('mouseout', () => {
                tooltip.style('display', 'none');
            });

    }, [data, portfolioData, benchmarkData, hasCombinedStrategy, theme, width, hoverLine, isLogScale]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden', position: 'relative' }}>
            <Tooltip title={`Switch to ${isLogScale ? 'linear' : 'logarithmic'} scale`}>
                <IconButton
                    onClick={() => setIsLogScale(!isLogScale)}
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        zIndex: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        color: theme.palette.text.primary,
                        width: 28,
                        height: 28,
                        fontSize: '12px',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                        }
                    }}
                >
                    {isLogScale ? 'LIN' : 'LOG'}
                </IconButton>
            </Tooltip>

            {/* Legend */}
            <Box sx={{
                position: 'absolute',
                top: 10,
                left: 60,
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 1,
                padding: 1,
                display: 'flex',
                gap: 2,
                fontSize: '11px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                        width: 20,
                        height: 2,
                        backgroundColor: theme.palette.text.primary,
                    }} />
                    <span>Strategy</span>
                </Box>
                {portfolioData && portfolioData.chartData.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 20,
                            height: 2,
                            backgroundColor: theme.palette.primary.main,
                        }} />
                        <span>Portfolio</span>
                    </Box>
                )}
                {benchmarkData && benchmarkData.chartData.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 20,
                            height: 2,
                            borderTop: `2px dashed ${theme.palette.warning.main}`,
                            borderBottom: 'none',
                        }} />
                        <span>Benchmark</span>
                    </Box>
                )}
            </Box>

            <svg
                ref={svgRef}
                width={width}
                height={400}
                style={{ background: 'transparent' }}
            />
        </Box>
    );
};
